import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import { useSelector } from "react-redux";
import { asList, useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { thunkFetchLoginMethods } from "../../store/markets/actions";
import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { AppState } from "../../store";
import { LoginMethod } from "../../models/Market";

interface LoginMethodMultiSelectProps extends Omit<SelectProps, "loading"> {
  label?: string;
  onSelected: (value: LoginMethod[]) => void;
  variant?: "filled" | "outlined" | "standard";
  value: Array<LoginMethod>;
  onlyAllowsIdSwitching?: boolean;
}

export const VerificationMethodMultiSelect: FunctionComponent<
  LoginMethodMultiSelectProps
> = ({
  label = "LoginMethod",
  onSelected,
  onChange,
  variant = "standard",
  value = [] as Array<LoginMethod>,
  ...props
}) => {
  const allLoginMethods = asList<LoginMethod>(
    useSelector<AppState, unknown>(s => s.loginMethods)
  );
  const loginMethods = allLoginMethods.filter(x => x.allowsIdSwitching);

  useLoadIfEmpty(!allLoginMethods.length, thunkFetchLoginMethods);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      const methodIds = (event.target.value as Array<number>) ?? [];

      const methods = methodIds.map(
        id => loginMethods.find(x => x.id === id) as LoginMethod
      );

      onSelected(methods ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (ids: ReadonlyArray<number>): ReactNode =>
    loginMethods
      .filter(x => ids.includes(x.id))
      .map(x => x.name)
      .join(", ");

  return (
    <FormControl
      style={props.style}
      variant={variant}
      className={props.className}
    >
      <InputLabel
        id="loginMethod-multi-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {label}
      </InputLabel>
      <Select
        multiple
        value={value.map(x => x.id)}
        onChange={handleChange}
        labelId="loginMethod-multi-select-label"
        id="loginMethod-multi-select"
        autoWidth={true}
        input={<Input />}
        disabled={props.disabled}
        renderValue={value => renderNames(value as ReadonlyArray<number>)}
      >
        {loginMethods.map((method, i) => (
          <MenuItem key={i} value={method.id}>
            <Checkbox checked={value.some(v => v.id === method.id)} />
            <ListItemText primary={method.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
